import React, { useEffect, useState } from "react";
import DynamicIcon from "../DynamicIcon";
import AddPoolElement from "../templates/AddPoolElement";
import CustomSelectElement from "../CustomSelectElement";
import { useSelector } from "react-redux";
import { selectAccountData } from "../../redux/main/accountSlice";
import { formatDurationMinutes, formatDistance } from "../../utils/formatUtils";
import { useColorPalette } from "../../hooks/useColorPalette";
import AddressSystemsModal from "./AddressSystemsModal";
import axios from "axios";
import { getNodeApiUrl } from "../../config/api.config.js";

const initialState = {
    fiscal: false,
    alias: "",
    addressTypeLabel: "",
    address: "",
    phone: "",
    mobile: "",
    email: ""
}

export default function CustomerAddressesPool({ arrayData, addEditAddress, deleteAddress }) {
    // Hook per gestire i colori dinamici del tema
    const palette = useColorPalette();
    
    // Stati per la gestione della modale e degli impianti associati
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [associatedSystems, setAssociatedSystems] = useState([]);
    const [pendingDeleteInfo, setPendingDeleteInfo] = useState(null);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [isChecking, setIsChecking] = useState(false);
    // Impianti collegati all'indirizzo in modifica (per il presidio UX correzione vs nuovo indirizzo)
    const [editAssociatedSystems, setEditAssociatedSystems] = useState([]);
    
    // Ottieni il token di autenticazione e i dati dell'account
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const accountData = useSelector(selectAccountData);
    
    // Funzione per verificare se un indirizzo è associato a impianti
    const checkAddressSystemsAssociation = async (addressId) => {
        console.log('[DEBUG] checkAddressSystemsAssociation - Verifica per indirizzo ID:', addressId);
        console.log('[DEBUG] checkAddressSystemsAssociation - Account ID:', accountData?._id);
        
        if (!addressId || !accountData?._id) {
            console.log('[DEBUG] checkAddressSystemsAssociation - Parametri mancanti, impossibile procedere');
            return [];
        }
        
        setIsChecking(true);
        try {
            const apiUrl = getNodeApiUrl('systems/checkAddressAssociation');
            console.log('[DEBUG] checkAddressSystemsAssociation - Chiamando API:', apiUrl);
            
            const response = await axios.get(
                apiUrl, {
                    params: {
                        customerAddressId: addressId,
                        accountId: accountData._id
                    },
                    headers: { 'auth-token': token }
                }
            );
            
            console.log('[DEBUG] checkAddressSystemsAssociation - Risposta API:', response.data);
            setIsChecking(false);
            
            // La risposta può avere una delle due strutture seguenti:
            // 1. {result: {systems: [...]}}
            // 2. {systems: [...]} - struttura attuale dalla risposta
            let systemsData = [];
            
            if (response.data.result && response.data.result.systems) {
                // Struttura 1
                systemsData = response.data.result.systems;
                console.log(`[DEBUG] checkAddressSystemsAssociation - Trovati ${systemsData.length} sistemi nella struttura result.systems`);
            } else if (response.data.systems) {
                // Struttura 2
                systemsData = response.data.systems;
                console.log(`[DEBUG] checkAddressSystemsAssociation - Trovati ${systemsData.length} sistemi nella struttura systems`);
            }
            
            if (systemsData && systemsData.length > 0) {
                return systemsData;
            }
            
            console.log('[DEBUG] checkAddressSystemsAssociation - Nessun sistema trovato nella risposta API');
            return [];
        } catch (error) {
            console.error('[ERROR] checkAddressSystemsAssociation - Errore:', error);
            setIsChecking(false);
            return [];
        }
    };
    
    // Funzione wrapper per gestire l'eliminazione e impostare automaticamente fiscal=true se rimane un solo indirizzo
    const handleDeleteAddress = async (field, index) => {
        console.log('[DEBUG] handleDeleteAddress - Inizio eliminazione:', { field, index });
        
        // Verifica se l'indirizzo ha un ID (potrebbe essere un indirizzo appena aggiunto)
        const addressToDelete = arrayData[index];
        console.log('[DEBUG] handleDeleteAddress - Indirizzo da eliminare:', addressToDelete);
        
        if (!addressToDelete || !addressToDelete._id) {
            console.log('[DEBUG] handleDeleteAddress - Indirizzo senza ID, eliminazione diretta');
            // Anche per indirizzi senza ID mostriamo la modale di conferma
            setAssociatedSystems([]);
            setPendingDeleteInfo({ field, index });
            setAddressToDelete(addressToDelete);
            setIsModalOpen(true);
            return;
        }
        
        console.log('[DEBUG] handleDeleteAddress - Controllo associazioni per indirizzo ID:', addressToDelete._id);
        // Controlla se ci sono impianti associati all'indirizzo
        const systems = await checkAddressSystemsAssociation(addressToDelete._id);
        console.log('[DEBUG] handleDeleteAddress - Risultato controllo:', { trovati: systems?.length || 0, systems });
        
        // In tutti i casi mostriamo la modale, con o senza impianti associati
        setAssociatedSystems(systems || []);
        setPendingDeleteInfo({ field, index });
        setAddressToDelete(addressToDelete);
        setIsModalOpen(true);
        console.log('[DEBUG] handleDeleteAddress - Stato modale dopo impostazione:', { 
            isModalOpen: true, 
            associatedSystems: systems,
            addressToDelete: addressToDelete
        });
    };
    
    // Funzione per procedere con l'eliminazione effettiva
    const proceedWithDelete = (field, index) => {
        // Prima elimina l'indirizzo
        deleteAddress(field, index);
        
        // Se dopo l'eliminazione rimarrebbe solo un elemento nell'array
        if (arrayData.length === 2) {
            // Determina l'indice dell'elemento che rimarrà
            const remainingIndex = index === 0 ? 1 : 0;
            const remainingAddress = {...arrayData[remainingIndex]};
            
            // Se quell'elemento non è già impostato come indirizzo di fatturazione, impostiamolo
            if (!remainingAddress.fiscal) {
                remainingAddress.fiscal = true;
                console.log('Impostazione automatica fiscal=true sull\'ultimo indirizzo rimasto');
                
                // Aggiorna l'elemento con fiscal=true
                setTimeout(() => {
                    addEditAddress(field, remainingAddress, remainingIndex);
                }, 100); // Piccolo timeout per assicurarsi che l'eliminazione sia completata
            }
        }
    };
    // Otteniamo i dati dell'account per accedere ai tipi di indirizzo configurati
    // accountData è già dichiarato sopra
    // Gestisco l'apertura e chiusura del form
    // Questo stato passerà ai figli
    const [isAdding, setIsAdding] = useState(false);
    // Gestisco gli input del form
    const [formInput, setFormInput] = useState(initialState);
    const handleFormInput = (event) => {
        const { id, value, checked } = event?.target;
        const fixedValue = id === 'fiscal' ? checked : value;
        setFormInput(prevState => ({
            ...prevState,
            [id]: fixedValue
        }))
    }
    // Gestisco la modalità edit
    const editInitialState = {
        isEditMode: true,
        index: null
    }
    const [edit, setEdit] = useState(editInitialState)
    // Gestisco il settaggio di edit
    const handleSetEdit = async (index) => {
        setEdit({
            isEditMode: true,
            index
        });
        setIsAdding(true);

        // Presidio UX: se l'indirizzo esistente ha impianti collegati, avvisa che la
        // modifica è una CORREZIONE (propagata a impianti/documenti). Per un cambio reale
        // occorre aggiungere un nuovo indirizzo.
        const addressToEdit = arrayData[index];
        if (addressToEdit && addressToEdit._id) {
            const systems = await checkAddressSystemsAssociation(addressToEdit._id);
            setEditAssociatedSystems(systems || []);
        } else {
            setEditAssociatedSystems([]);
        }
    }
    // Se edit è impostato, imposto i valori dell'oggetto all'indice specificato in formInput
    useEffect(() => {
        if (edit.isEditMode && edit.index !== null) {
            setFormInput(arrayData[edit.index])
        }
    }, [edit]);
    // Gestisco l'annullamento della modalità edit (tornando alla modalità add e chiudendo il dropdown)
    const handleCancelEdit = () => {
        setEdit(editInitialState);
        setFormInput(initialState);
        setIsAdding(false);
        setEditAssociatedSystems([]);
    }
    // Se chiudo la modifica con lo switch, azzero formInput ed esco dalla modalità edit
    useEffect(() => {
        if (!isAdding) {
            setEdit(editInitialState);
            setFormInput(initialState);
            setEditAssociatedSystems([]);
        }
    }, [isAdding])

    // Controllo che siano inseriti almeno alias e indirizzo
    const [isAllField, setIsAllField] = useState(false);
    const checkAllField = () => {
        const requiredFields = ['alias', 'address'];
        const allRequiredFieldsValid = requiredFields.every(field => {
            const value = (formInput)[field];
            return value && value.trim() !== '';
        });
        setIsAllField(allRequiredFieldsValid);
    }
    useEffect(() => {
        checkAllField();
    }, [formInput])

    // Gestisco il tasto aggiungi, il quale deve anche chiudere la tendina di aggiunta indirizzo
    const handleFormSubmit = () => {
        // Crea una copia del formInput che potrebbe essere modificata
        let updatedFormInput = {...formInput};
        
        // Se è l'unico indirizzo (nessun elemento nell'array o solo questo in modalità modifica)
        if (!arrayData || arrayData.length === 0 || (arrayData.length === 1 && edit.isEditMode)) {
            // Imposta automaticamente fiscal a true
            updatedFormInput.fiscal = true;
            console.log('Impostazione automatica fiscal=true perché è l\'unico indirizzo');
        }
        
        if (edit.isEditMode && edit.index !== null) {
            addEditAddress('addresses', updatedFormInput, edit.index);
            setFormInput(initialState);
            setIsAdding(false);
        } else {
            addEditAddress('addresses', updatedFormInput);
            setFormInput(initialState);
            setIsAdding(false);
        }
    }
    return (
        <div className="w-full bg-slate-200 p-1">
            {/* Sezione intestazione con titolo e pulsante */}
            <div className="bg-white p-4 flex justify-between items-center">
                <h5 className="text-lg font-medium text-gray-700">Indirizzi</h5>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    type="button"
                    style={{ 
                        backgroundColor: palette.primary,
                        color: palette.textOnPrimary
                    }}
                    className="flex items-center px-3 py-2 text-sm font-medium border border-transparent rounded-md shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                    <i className="fa-solid fa-plus mr-2"></i>
                    {edit.isEditMode && edit.index !== null ? 'MODIFICA INDIRIZZO' : 'AGGIUNGI INDIRIZZO'}
                </button>
            </div>

            {/* Form per l'aggiunta/modifica dell'indirizzo */}
            <div className={`bg-white overflow-hidden transition-all duration-300 ${isAdding ? 'max-h-[2000px] opacity-100 p-4 border-t border-gray-200' : 'max-h-0 opacity-0 p-0'}`}>
                    <div className="mt-4 flex flex-col gap-4">
                        {/* Presidio UX: avviso correzione vs nuovo indirizzo */}
                        {edit.isEditMode && edit.index !== null && editAssociatedSystems.length > 0 && (
                            <div className="alert alert-info mb-0" role="alert">
                                <div className="d-flex align-items-start">
                                    <i className="fa-solid fa-circle-info me-3 mt-1 fs-5"></i>
                                    <div>
                                        <strong>Stai correggendo un indirizzo collegato a {editAssociatedSystems.length} {editAssociatedSystems.length === 1 ? 'impianto' : 'impianti'}.</strong>
                                        <div className="mt-1">
                                            La modifica verrà propagata a tutti gli impianti e ai documenti collegati (commesse, assistenze, manutenzioni, appuntamenti),
                                            incluse coordinate e distanza dalla sede (km/minuti).
                                            Se invece la sede è <strong>cambiata davvero</strong>, annulla e <strong>aggiungi un nuovo indirizzo</strong> anziché modificare questo.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2 items-center">
                            <div className={`pl-4 flex gap-2 p-2 border rounded-lg ${formInput.fiscal ? 'border-green-400' : 'border-transparent'}`}>
                                <input type="checkbox" id="fiscal" checked={formInput.fiscal} onChange={handleFormInput} />
                                <label htmlFor="fiscal">Indirizzo di fatturazione</label>
                            </div>
                            
                            {formInput.geoData && (
                                <div className="ml-4 p-2 bg-gray-100 rounded-lg flex items-center text-sm">
                                    <i className="fa-solid fa-location-dot text-red-500 mr-2"></i>
                                    <span>
                                        {formInput.geoData.distanceKm > 0 || formInput.geoData.distanceTime > 0 ? (
                                            <>
                                                Distanza dalla sede: <strong>{formatDistance(formInput.geoData.distanceKm)}</strong> ({formatDurationMinutes(formInput.geoData.distanceTime)})
                                            </>
                                        ) : (
                                            'Calcolo distanza in corso...'
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1"><label htmlFor="alias">Alias (Campo obbligatorio)</label><input type="text" id="alias" onChange={handleFormInput} value={formInput.alias} /></div>
                            <div className="input-box flex-1">
                                <label htmlFor="addressTypeLabel">Tipo di indirizzo</label>
                                {accountData?.settings?.addressTypes ? (
                                    <CustomSelectElement
                                        id="addressTypeLabel"
                                        name="addressTypeLabel"
                                        list={accountData.settings.addressTypes}
                                        setter={(value) => {
                                            setFormInput(prev => ({
                                                ...prev,
                                                addressTypeLabel: value
                                            }));
                                        }}
                                        defaultValue={formInput.addressTypeLabel}
                                        valueKey="label"
                                        labelKey="label"
                                    />
                                ) : (
                                    <input 
                                        type="text" 
                                        id="addressTypeLabel" 
                                        onChange={handleFormInput} 
                                        value={formInput.addressTypeLabel} 
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1"><label htmlFor="address">Indirizzo (Campo obbligatorio)</label><input type="text" id="address" onChange={handleFormInput} value={formInput.address} /></div>
                        </div>
                        
                        {/* Nuovi campi: telefono, mobile ed email */}
                        <div className="flex gap-2">
                            <div className="input-box flex-1">
                                <label htmlFor="phone">Telefono</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-phone"></i></span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        id="phone" 
                                        onChange={handleFormInput} 
                                        value={formInput.phone || ""} 
                                        placeholder="Inserisci telefono"
                                    />
                                </div>
                            </div>
                            <div className="input-box flex-1">
                                <label htmlFor="mobile">Cellulare</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-mobile-alt"></i></span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        id="mobile" 
                                        onChange={handleFormInput} 
                                        value={formInput.mobile || ""} 
                                        placeholder="Inserisci cellulare"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="input-box flex-1">
                                <label htmlFor="email">Email</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="fa-solid fa-envelope"></i></span>
                                    <input 
                                        type="email" 
                                        className="form-control" 
                                        id="email" 
                                        onChange={handleFormInput} 
                                        value={formInput.email || ""} 
                                        placeholder="Inserisci email"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="w-full flex justify-end mt-4 mb-4 pb-2">
                            {
                                isAllField ? (
                                    <div className="flex gap-2">
                                        {/* Pulsante Annulla (sempre presente) */}
                                        <button 
                                            type="button"
                                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors" 
                                            onClick={() => {
                                                if (edit.isEditMode) {
                                                    handleCancelEdit();
                                                } else {
                                                    setIsAdding(false);
                                                    setFormInput(initialState);
                                                }
                                            }}
                                        >
                                            <i className="fa-solid fa-times mr-2"></i>Annulla
                                        </button>
                                        
                                        {/* Pulsante Aggiungi/Aggiorna in base al contesto */}
                                        <button 
                                            type="button"
                                            style={{ 
                                                backgroundColor: edit.isEditMode && edit.index !== null ? palette.secondary : palette.primary,
                                                color: edit.isEditMode && edit.index !== null ? palette.textOnSecondary : palette.textOnPrimary
                                            }}
                                            className="px-4 py-2 rounded-md transition-colors hover:opacity-90"
                                            onClick={handleFormSubmit}
                                        >
                                            {edit.isEditMode && edit.index !== null ? (
                                                <><i className="fa-solid fa-check mr-2"></i>Aggiorna</>
                                            ) : (
                                                <><i className="fa-solid fa-plus mr-2"></i>Aggiungi</>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        type="button"
                                        className="px-4 py-2 text-gray-500 bg-gray-200 rounded-md cursor-not-allowed opacity-60"
                                        disabled
                                    >
                                        <i className="fa-solid fa-ban mr-2"></i>
                                        {edit.isEditMode && edit.index !== null ? 'Aggiorna' : 'Aggiungi'}
                                    </button>
                                )
                            }
                        </div>
                    </div>
            </div>
            {/* POOL - Tabella di rendering elementi array */}
            <div className="p-4">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="h-8">
                            <th className="w-12">#</th>
                            <th className="">ALIAS</th>
                            <th className="">TIPO</th>
                            <th className="">INDIRIZZO</th>
                            <th className="">TELEFONO</th>
                            <th className="">CELLULARE</th>
                            <th className="">EMAIL</th>
                            <th className="">SEDE LEGALE</th>
                            <th className="">MODIFICA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            arrayData?.map((row, index) => {
                                return (
                                    < React.Fragment key={index} >
                                        <tr className="h-8 border-t border-slate-500 border-dashed">
                                            <td className="w-12">{index + 1}</td>
                                            <td className="">{row.alias}</td>
                                            <td className="">{row.addressTypeLabel}</td>
                                            <td className="">{row.address}</td>
                                            <td className="">{row.phone || '-'}</td>
                                            <td className="">{row.mobile || '-'}</td>
                                            <td className="">{row.email || '-'}</td>
                                            <td className="">{row.fiscal ? 'true' : 'false'}</td>
                                            <td className="flex gap-2">
                                                <div onClick={() => handleSetEdit(index)}><DynamicIcon iconClass="fa-solid fa-pen-to-square" /></div>
                                                <div onClick={() => handleDeleteAddress('addresses', index)}><DynamicIcon iconClass="fa-solid fa-trash" /></div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )

                            })
                        }
                    </tbody>
                </table>
            </div>
            
            {/* Modale per conferma eliminazione indirizzo */}
            <AddressSystemsModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(!isModalOpen)}
                systems={associatedSystems}
                addressToDelete={addressToDelete}
                onConfirm={() => {
                    if (pendingDeleteInfo) {
                        proceedWithDelete(pendingDeleteInfo.field, pendingDeleteInfo.index);
                        setPendingDeleteInfo(null);
                        setAddressToDelete(null);
                    }
                }}
            />
            
            {/* Indicatore di caricamento durante il controllo */}
            {isChecking && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 1050 }}>
                    <div className="bg-white p-3 rounded shadow d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Caricamento...</span>
                        </div>
                        <span>Verifica associazioni in corso...</span>
                    </div>
                </div>
            )}
        </div >
    )
}